import React, { useState, useMemo, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  BookOpen, 
  Search, 
  Filter,
  ExternalLink,
  Tag,
  List,
  Layers,
  Quote,
  FileText
} from 'lucide-react';
import { glossaryEntries, conceptualGroups, searchGlossary, type GlossaryEntry } from '../data/glossary';

type ViewMode = 'alphabetical' | 'conceptual';

// Map of terms that have definition detail pages
const definitionPages: Record<string, string> = {
  'Canon Engine': '/definition/canon-engine',
  // Add more as they're created
};

// Helper to get definition page path for a term
const getDefinitionPagePath = (term: string): string | null => {
  return definitionPages[term] || null;
};

export const Glossary: React.FC = () => {
  const location = useLocation();
  const [viewMode, setViewMode] = useState<ViewMode>('alphabetical');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<GlossaryEntry['category'] | 'all'>('all');
  const [selectedLetter, setSelectedLetter] = useState<string>('all');

  // Handle hash navigation (e.g., /glossary#term-name)
  useEffect(() => {
    if (location.hash) {
      const termFromHash = location.hash.substring(1).trim(); // Remove the # and avoid empty ID
      if (!termFromHash) return;
      // Find the entry that matches this hash
      const entry = glossaryEntries.find(e => e.term.toLowerCase().replace(/\s+/g, '-') === termFromHash);
      if (entry) {
        // Clear all filters to ensure the term is visible
        setSearchTerm(entry.term);
        setSelectedCategory('all');
        setSelectedLetter('all');
        // Scroll to the term after a brief delay
        setTimeout(() => {
          const termElement = document.getElementById(termFromHash);
          if (termElement) {
            termElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      }
    }
  }, [location.hash]);

  const categories: Array<{ value: GlossaryEntry['category'] | 'all', label: string, color: string }> = [
    { value: 'all', label: 'All', color: 'gray' },
    { value: 'framework', label: 'Frameworks', color: 'blue' },
    { value: 'concept', label: 'Concepts', color: 'purple' },
    { value: 'technique', label: 'Techniques', color: 'green' },
    { value: 'principle', label: 'Principles', color: 'indigo' },
    { value: 'anti-pattern', label: 'Anti-Patterns', color: 'red' }
  ];

  const filteredEntries = useMemo(() => {
    let entries = glossaryEntries;

    // Search filter
    if (searchTerm) {
      entries = searchGlossary(searchTerm);
    }

    // Category filter
    if (selectedCategory !== 'all') {
      entries = entries.filter(e => e.category === selectedCategory);
    }

    // Letter filter
    if (selectedLetter !== 'all') {
      entries = entries.filter(e => e.term.charAt(0).toUpperCase() === selectedLetter);
    }

    // Sort alphabetically
    return entries.sort((a, b) => a.term.localeCompare(b.term));
  }, [searchTerm, selectedCategory, selectedLetter]);

  // Get all unique first letters
  const availableLetters = useMemo(() => {
    const letters = new Set<string>();
    glossaryEntries.forEach(entry => {
      letters.add(entry.term.charAt(0).toUpperCase());
    });
    return Array.from(letters).sort();
  }, []);

  const getCategoryColor = (category: GlossaryEntry['category']) => {
    const cat = categories.find(c => c.value === category);
    return cat?.color || 'gray';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <BookOpen className="h-8 w-8 text-consulting-slate-blue-600" />
            <h1 className="text-3xl font-bold text-charcoal-900">Glossary</h1>
          </div>
          <p className="text-charcoal-600">
            Canonical definitions of key terms, concepts, and frameworks in Peak Priorities Base Camp
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-sm text-charcoal-600">
            <span className="font-bold text-charcoal-900">{filteredEntries.length}</span> {filteredEntries.length === 1 ? 'entry' : 'entries'}
          </div>
          {/* View Mode Toggle */}
          <div className="flex gap-2 bg-charcoal-100 p-1">
            <button
              onClick={() => setViewMode('alphabetical')}
              className={`
                flex items-center gap-2 px-4 py-2  text-sm font-medium transition-all
                ${viewMode === 'alphabetical'
                  ? 'bg-[var(--bg-raised)] text-consulting-slate-blue-700 shadow-sm'
                  : 'text-charcoal-600 hover:text-charcoal-900'
                }
              `}
            >
              <List className="h-4 w-4" />
              Alphabetical
            </button>
            <button
              onClick={() => setViewMode('conceptual')}
              className={`
                flex items-center gap-2 px-4 py-2  text-sm font-medium transition-all
                ${viewMode === 'conceptual'
                  ? 'bg-[var(--bg-raised)] text-sage-700 shadow-sm'
                  : 'text-charcoal-600 hover:text-charcoal-900'
                }
              `}
            >
              <Layers className="h-4 w-4" />
              By Concept
            </button>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-4">
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[var(--text-ter)]" />
            <input
              type="text"
              placeholder="Search glossary terms and definitions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-3 w-full border border-[var(--rule)] focus:ring-2 focus:ring-consulting-slate-blue-500 focus:border-consulting-slate-blue-500"
            />
          </div>
        </div>

        {/* Category Pills */}
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <Filter className="h-4 w-4 text-[var(--text-ter)]" />
          <span className="text-sm font-medium text-charcoal-700">Category:</span>
          {categories.map(cat => (
            <button
              key={cat.value}
              onClick={() => setSelectedCategory(cat.value)}
              className={`
                px-3 py-1 rounded-full text-xs font-medium transition-colors
                ${selectedCategory === cat.value
                  ? `bg-${cat.color}-600 text-white`
                  : `bg-${cat.color}-100 text-${cat.color}-800 hover:bg-${cat.color}-200`
                }
              `}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Letter Navigation */}
        <div className="flex items-center gap-1 flex-wrap">
          <span className="text-sm font-medium text-charcoal-700 mr-2">Letter:</span>
          <button
            onClick={() => setSelectedLetter('all')}
            className={`
              px-2 py-1 rounded text-xs font-medium transition-colors
              ${selectedLetter === 'all'
                ? 'bg-charcoal-600 text-white'
                : 'bg-[var(--hover-tint)] text-charcoal-700 hover:bg-[var(--hover-tint)]'
              }
            `}
          >
            All
          </button>
          {availableLetters.map(letter => (
            <button
              key={letter}
              onClick={() => setSelectedLetter(letter)}
              className={`
                px-2 py-1 rounded text-xs font-medium transition-colors
                ${selectedLetter === letter
                  ? 'bg-consulting-slate-blue-600 text-white'
                  : 'bg-[var(--hover-tint)] text-charcoal-700 hover:bg-[var(--hover-tint)]'
                }
              `}
            >
              {letter}
            </button>
          ))}
        </div>
      </div>

      {/* Glossary Entries */}
      {filteredEntries.length === 0 && viewMode === 'alphabetical' ? (
        <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-12 text-center">
          <Search className="h-12 w-12 text-[var(--text-ter)] mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-charcoal-900 mb-2">No Entries Found</h3>
          <p className="text-charcoal-600">
            Try adjusting your search or filters
          </p>
        </div>
      ) : viewMode === 'alphabetical' ? (
        <div className="space-y-4">
          {filteredEntries.map((entry, index) => {
            const color = getCategoryColor(entry.category);
            const entryId = entry.term.toLowerCase().replace(/\s+/g, '-');
            
            return (
              <div 
                key={index} 
                id={entryId}
                className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] hover:shadow-md transition-shadow scroll-mt-4"
              >
                <div className="flex">
                  {/* Left Column - Book Icon */}
                  {entry.source === 'Managing Priorities' && (
                    <div className="flex-shrink-0 p-6 pr-4 flex items-start">
                      <img 
                        src="/images/managing-priorities-book.jpg" 
                        alt="Managing Priorities book" 
                        className="h-32 w-auto object-contain"
                        title="Definition from Managing Priorities book"
                      />
                    </div>
                  )}
                  
                  {/* Right Column - Content */}
                  <div className="flex-1 p-6">
                    {/* Term Header */}
                    <div className="mb-3">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-xl font-bold text-charcoal-900">
                          {getDefinitionPagePath(entry.term) ? (
                            <Link 
                              to={getDefinitionPagePath(entry.term)!}
                              className="hover:text-consulting-slate-blue-600 transition-colors"
                            >
                              {entry.term}
                            </Link>
                          ) : (
                            entry.term
                          )}
                        </h3>
                        {getDefinitionPagePath(entry.term) && (
                          <Link
                            to={getDefinitionPagePath(entry.term)!}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-consulting-slate-blue-50 text-consulting-slate-blue-700 hover:bg-consulting-slate-blue-100 transition-colors"
                            title="View detailed definition page"
                          >
                            <FileText className="h-3 w-3" />
                            Details
                          </Link>
                        )}
                        {entry.source === 'Managing Priorities' && entry.qualityLevel === 1 && (
                          <Quote 
                            className="h-6 w-6 text-[var(--mustard)]" 
                            title="Verbatim quote from Managing Priorities book"
                          />
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-${color}-100 text-${color}-800`}>
                          <Tag className="h-3 w-3" />
                          {entry.category}
                        </span>
                        {entry.relatedFramework && (
                          <Link
                            to={`/elevation/entry/${entry.relatedFramework}`}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-consulting-slate-blue-50 text-consulting-slate-blue-700 hover:bg-consulting-slate-blue-100 transition-colors"
                          >
                            <ExternalLink className="h-3 w-3" />
                            View Framework
                          </Link>
                        )}
                      </div>
                    </div>

                    {/* Definition */}
                    <p className="text-charcoal-700 mb-4 leading-relaxed">
                      {entry.definition}
                    </p>

                    {/* Examples */}
                    {entry.examples && entry.examples.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-sm font-semibold text-charcoal-900 mb-2">Examples:</h4>
                        <ul className="space-y-2">
                          {entry.examples.map((example, idx) => (
                            <li key={idx} className="text-sm text-charcoal-600 flex items-start">
                              <span className="text-consulting-slate-blue-600 mr-2 flex-shrink-0">•</span>
                              <span className="italic">{example}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* See Also */}
                    {entry.seeAlso && entry.seeAlso.length > 0 && (
                      <div className="pt-4 border-t border-[var(--rule)]">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-charcoal-700">See also:</span>
                          {entry.seeAlso.map((term, idx) => {
                            const definitionPath = getDefinitionPagePath(term);
                            if (definitionPath) {
                              // If term has a definition page, link directly to it
                              return (
                                <Link
                                  key={idx}
                                  to={definitionPath}
                                  className="text-sm text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700 hover:underline inline-flex items-center gap-1"
                                >
                                  {term}
                                  <FileText className="h-3 w-3" />
                                </Link>
                              );
                            } else {
                              // Otherwise, search and scroll to it in the glossary
                              return (
                                <button
                                  key={idx}
                                  onClick={() => {
                                    // Clear all filters to ensure the linked term is visible
                                    setSearchTerm(term);
                                    setSelectedCategory('all');
                                    setSelectedLetter('all');
                                    // Scroll to the term after a brief delay to allow re-render
                                    setTimeout(() => {
                                      const id = term?.toLowerCase().replace(/\s+/g, '-');
                                      if (!id) return;
                                      const termElement = document.getElementById(id);
                                      if (termElement) {
                                        termElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                      }
                                    }, 100);
                                  }}
                                  className="text-sm text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700 hover:underline"
                                >
                                  {term}
                                </button>
                              );
                            }
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* Conceptual/Family View */
        <div className="space-y-6">
          {conceptualGroups.map((group, groupIdx) => (
            <div key={groupIdx} className="bg-[var(--bg-raised)] shadow-lg border-2 border-sage-200 overflow-hidden">
              <div className="bg-gradient-to-r from-sage-50 to-consulting-slate-blue-50 px-6 py-4 border-b-2 border-sage-200">
                <h2 className="text-2xl font-bold text-charcoal-900">{group.name}</h2>
                <p className="text-charcoal-700 mt-1">{group.description}</p>
                {group.relatedFramework && (
                  <Link
                    to={`/elevation/entry/${group.relatedFramework}`}
                    className="inline-flex items-center gap-1 mt-2 px-3 py-1 text-sm font-medium bg-consulting-slate-blue-600 text-white hover:bg-consulting-slate-blue-700 transition-colors"
                  >
                    <ExternalLink className="h-3 w-3" />
                    View Framework
                  </Link>
                )}
              </div>
              <div className="p-6 space-y-4">
                {group.terms.map((term, termIdx) => {
                  const entry = glossaryEntries.find(e => e.term === term);
                  if (!entry) return null;
                  
                  const color = getCategoryColor(entry.category);
                  
                  return (
                    <div key={termIdx} className="border-l-4 border-sage-400">
                      <div className="flex">
                        {/* Left Column - Book Icon */}
                        {entry.source === 'Managing Priorities' && (
                          <div className="flex-shrink-0 p-4 pr-3 flex items-start">
                            <img 
                              src="/images/managing-priorities-book.jpg" 
                              alt="Managing Priorities book" 
                              className="h-32 w-auto object-contain"
                              title="Definition from Managing Priorities book"
                            />
                          </div>
                        )}
                        
                        {/* Right Column - Content */}
                        <div className="flex-1 pl-4 py-2">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="text-lg font-bold text-charcoal-900">{entry.term}</h3>
                            {entry.source === 'Managing Priorities' && entry.qualityLevel === 1 && (
                              <Quote 
                                className="h-6 w-6 text-[var(--mustard)]" 
                                title="Verbatim quote from Managing Priorities book"
                              />
                            )}
                          </div>
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-${color}-100 text-${color}-800 mb-2`}>
                            {entry.category}
                          </span>
                          <p className="text-charcoal-700 leading-relaxed">
                            {entry.definition}
                          </p>
                          {entry.examples && entry.examples.length > 0 && (
                            <div className="mt-2">
                              <ul className="space-y-1">
                                {entry.examples.slice(0, 2).map((example, idx) => (
                                  <li key={idx} className="text-sm text-charcoal-600 flex items-start">
                                    <span className="text-sage-600 mr-2 flex-shrink-0">•</span>
                                    <span className="italic">{example}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info Box */}
      <div className="bg-consulting-slate-blue-50 p-6 border-2 border-consulting-slate-blue-200">
        <h3 className="font-bold text-consulting-slate-blue-900 mb-2">About the Glossary</h3>
        <p className="text-consulting-slate-blue-800 text-sm">
          This glossary provides canonical definitions for key terms used throughout Peak Priorities Base Camp. 
          Each entry is categorized and linked to related frameworks when applicable. Use this as a reference when exploring 
          the M2 Models, M1 Templates, and M0 Runtime instances to ensure shared understanding and precise communication.
        </p>
      </div>
    </div>
  );
};
